import type { ReactNode } from "react";
import { Tabs, Center, Text } from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

export type GroupTab = {
  value: string;
  label: string;
  permission?: string;
  element: ReactNode;
};

/**
 * Renders several existing pages as inner tabs of ONE sidebar section. Tabs the
 * admin lacks permission for are hidden; super_admin sees all. The active tab is
 * kept in the URL (?tab=) so refresh/deep-links work. Only the active tab mounts
 * (keepMounted={false}) so inactive pages don't fetch data.
 */
export function TabbedGroup({ tabs }: { tabs: GroupTab[] }) {
  const { admin } = useAuth();
  const [params, setParams] = useSearchParams();

  const perms: string[] = (admin as any)?.permissions || [];
  const isSuper = (admin as any)?.role === "super_admin";
  const allowed = tabs.filter(
    (t) => !t.permission || isSuper || perms.includes(t.permission),
  );

  if (allowed.length === 0) {
    return (
      <Center py="xl">
        <Text c="dimmed">You don't have access to any section here.</Text>
      </Center>
    );
  }

  const requested = params.get("tab");
  const active =
    requested && allowed.some((t) => t.value === requested)
      ? requested
      : allowed[0].value;

  return (
    <Tabs
      value={active}
      onChange={(v) => setParams({ tab: v || allowed[0].value }, { replace: true })}
      keepMounted={false}
    >
      <Tabs.List>
        {allowed.map((t) => (
          <Tabs.Tab key={t.value} value={t.value}>
            {t.label}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {allowed.map((t) => (
        <Tabs.Panel key={t.value} value={t.value} pt="md">
          {active === t.value ? t.element : null}
        </Tabs.Panel>
      ))}
    </Tabs>
  );
}
